import moduleStyles from "components/module/styles.module.css";

import React, { useEffect, useState, use, useImperativeHandle, Ref }  from 'react';
import { ModuleProps } from "components/props/ModuleProps"
import { IconPicker } from "components/layout/icons/IconPicker";
import { ResponsiveContent } from "components/layout/ResponsiveContent";
import Spacer from "components/layout/Spacer";
import { IncidentApiRepository } from "repositories/api/IncidentApiRepository";
import { isIncidentAwaitingProcessing, isIncidentBeingProcessed, isIncidentCorrectionNeeded, isIncidentDraft } from "../../../incident/helpers/IncidentHelpers";
import { ModuleOnline } from "components/module/ModuleOnline";
import { ModuleWithData } from "components/module/ModuleWithData";

interface PromiseProps {
    countTotal: number;
    countDraft: number;
    countAwaitingProcessing: number;
    countBeingProcessed: number;
    countCorrectionNeeded: number;
}

const DefaultPromiseProps: PromiseProps = {
    countTotal: 0,
    countDraft: 0,
    countAwaitingProcessing: 0,
    countBeingProcessed: 0,
    countCorrectionNeeded: 0
}

export interface ModuleIncidentHandle {
    onRefresh: () => void;
}

interface ModuleIncidentProps {
    ref: Ref<ModuleIncidentHandle>;
    module: ModuleProps;
    onClick: (module: ModuleProps) => void;
}

export const ModuleIncident = ({ ref, module, onClick }: ModuleIncidentProps) => {

    useImperativeHandle(ref, () => ({
        onRefresh() {
            setDataPromise(fetchData());
        }
    }));

    const [dataPromise, setDataPromise] = useState<null | Promise<PromiseProps>>(null);

    const fetchOnline = async () => {
        const response = await IncidentApiRepository.searchAsync({});
        const data = response instanceof Response ? await response.json() : [];

        const countDraft = data.filter((d: any) => isIncidentDraft(d)).length;
        const countAwaitingProcessing = data.filter((d: any) => isIncidentAwaitingProcessing(d)).length;
        const countBeingProcessed = data.filter((d: any) => isIncidentBeingProcessed(d)).length;
        const countCorrectionNeeded = data.filter((d: any) => isIncidentCorrectionNeeded(d)).length;

        return {
            countTotal: countDraft + countAwaitingProcessing + countBeingProcessed,
            countDraft: countDraft,
            countAwaitingProcessing: countAwaitingProcessing,
            countBeingProcessed: countBeingProcessed,
            countCorrectionNeeded: countCorrectionNeeded
        };
    }

    const fetchData = (): Promise<PromiseProps> => {
        if (navigator.onLine) {
            return fetchOnline();
        } else {
            return new Promise<PromiseProps>((resolve) => resolve(DefaultPromiseProps));
        }
    }

    useEffect(() => {
        const promise = fetchData();
        setDataPromise(promise);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <ModuleWithData
            module={module}
            dataPromise={dataPromise}
            errorText="Kunne ikke hente hendelser.">
            <ModuleIncidentContent
                module={module}
                dataPromise={dataPromise!}
                onClick={onClick} />
        </ModuleWithData>
    )
}

interface ModuleIncidentContentProps {
    dataPromise: Promise<PromiseProps>,
    module: ModuleProps;
    onClick: (module: ModuleProps) => void;
}
const ModuleIncidentContent = ({
    dataPromise,
    module,
    onClick
}: ModuleIncidentContentProps) => {
    const data = use<PromiseProps>(dataPromise);
    return (
        data &&
        <ModuleOnline module={module}>
            <div
                onClick={() => onClick(module)}
                    className={moduleStyles.bar}
                    style={{ backgroundColor: (data.countCorrectionNeeded === 0) ? "var(--home-default-bg)" : "var(--home-warning-bg)" }}>
                    <div className={moduleStyles.barContent}>
                    <div>
                        <div>
                                {data.countTotal > 0 ? `Hendelser (${data.countTotal})` : "Hendelser"}
                        </div>
                        <Spacer />
                            {data.countCorrectionNeeded > 0 && (
                            <div>
                                    <b>{data.countCorrectionNeeded} krever korrigering</b>
                            </div>
                        )}

                            {data.countDraft > 0 && <div>{data.countDraft} p&aring;begynt (kladd)</div>}

                            {data.countAwaitingProcessing > 0 && (
                                <div>{data.countAwaitingProcessing} klar til saksbehandling</div>
                        )}

                            {data.countBeingProcessed > 0 && <div>{data.countBeingProcessed} under behandling</div>}

                            {data.countDraft === 0 && data.countAwaitingProcessing === 0 && data.countBeingProcessed === 0 && (
                            <div>S&oslash;k blant hendelser</div>
                        )}
                        <ResponsiveContent minWidth={660}>Krever en bredde p&aring; mer enn 660px.</ResponsiveContent>
                    </div>
                    <div>
                        <IconPicker icon="warning" size={24} />
                    </div>
                </div>
            </div>
        </ModuleOnline>
    )
}
